import { useGetRestaurants } from 'common/hooks/useGeRestaurants/useGeRestaurants'
import { getDayKey } from 'common/utils/getDayKey'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
const phoneRegExp = /^([+]?[0-9\s-\(\)]{3,25})*$/i

export const useBookSchema = (disabledDays: string[] = []) => {
  const { t } = useTranslation()
  const { restaurant } = useGetRestaurants({ revalidateOnMount: false })

  const BookSchema = () =>
    Yup.object({
      name: Yup.string()
        .min(2)
        .required(`${t('field-required')}`),
      phone: Yup.string()
        .required(`${t('field-required')}`)
        .matches(phoneRegExp, `${t('invalid-phone')}`)
        .min(5)
        .max(20),
      guests: Yup.number()
        .min(1, `${t('invalid-count')}`)
        .max(50, `${t('max-count')}`)
        .required(`${t('field-required')}`),
      date: Yup.date()
        .required(`${t('field-required')}`)
        .nullable()
        .test('is-not-disabled', `${t('date-disabled')}`, value => {
          if (!value) return false
          const selectedDate = moment(value).format('YYYY-MM-DD')
          return !disabledDays.includes(selectedDate)
        })
        .test('is-today-or-future', `${t('invаlid-date')}`, value => {
          if (!value) return false
          const today = moment().startOf('day')
          const selected = moment(value).startOf('day')
          return selected.isSameOrAfter(today)
        }),
      time: Yup.string()
        .required(`${t('field-required')}`)
        .test('is-in-working-hours', `${t('past-date')}`, function (value) {
          const { date } = this.parent
          if (!value || !date) return false

          const dayKey = getDayKey(date)
          const workTime = restaurant?.workingTime?.[dayKey]
          if (!workTime) return false
          const openTime = workTime.start
          let closeTime = workTime.end

          closeTime = moment(closeTime, 'HH:mm')
            .subtract(30, 'minutes')
            .format('HH:mm')

          const [hour, minute] = value.split(':').map(Number)
          if (isNaN(hour) || isNaN(minute)) return false

          const selected = moment(date).set({
            hour,
            minute,
            second: 0,
            millisecond: 0,
          })

          const [openHour, openMinute] = openTime?.split(':').map(Number) || [
            10, 0,
          ]
          const [closeHour, closeMinute] = closeTime
            ?.split(':')
            .map(Number) || [21, 0]

          const open = moment(date).set({
            hour: openHour,
            minute: openMinute,
            second: 0,
            millisecond: 0,
          })
          const close = moment(date).set({
            hour: closeHour,
            minute: closeMinute,
            second: 0,
            millisecond: 0,
          })

          const isToday = moment(date).isSame(moment(), 'day')
          const now = moment()

          const isInRange =
            selected.isSameOrAfter(open) && selected.isSameOrBefore(close)
          const isFuture = !isToday || selected.isAfter(now)

          return isInRange && isFuture
        }),
    })
  return { BookSchema }
}
